import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { authAPI } from '../services/api'
import { trackLanguageChange } from '../utils/analytics'
import { createLogger } from '../utils/logger'

const logger = createLogger('useLanguageChange')

/**
 * useLanguageChange - Hook for handling language changes
 * 
 * Changes language with full persistence:
 * 1. Changes i18n language (triggers React re-renders)
 * 2. Automatically persists to localStorage via i18n's languageChanged event
 * 3. Saves to database for authenticated users (async, non-blocking)
 * 
 * @returns {Function} handleLanguageChange - Callback to change language
 */
export function useLanguageChange() {
  const { i18n } = useTranslation()
  const { isAuthenticated, updateProfile } = useAuth()

  const handleLanguageChange = useCallback(async (lng) => {
    const previousLanguage = i18n.language
    
    // Update i18n immediately (triggers localStorage update via languageChanged event)
    i18n.changeLanguage(lng)
    
    // Track analytics event
    trackLanguageChange(previousLanguage, lng, 'header', isAuthenticated)
    
    // For authenticated users, also save to database (silent mode - no toast)
    if (isAuthenticated) {
      try {
        await updateProfile({ language: lng }, { silent: true })
      } catch (error) {
        logger.error('Failed to update language in database', error)
        // Don't show error to user - UI language still works via localStorage
      }
    }
  }, [i18n, isAuthenticated, updateProfile])

  return handleLanguageChange
}
